import { createApp } from 'vue';
import vue3GoogleLogin from 'vue3-google-login';
import App from './App.vue';
import vuetify from './plugins/vuetify';
import i18n from './i18n';
import router from './router';
import { createPinia, Store } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { useAuthStore } from '@/store/useAuthStore';
import { GOOGLE_CLIENT_ID, SENTRY_ENV, SENTRY_URL, SERVER_URL } from '@/config/config';
import ApiClientError from '@/models/ApiClientError';
import './assets/global.scss';
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';
import * as Sentry from '@sentry/vue';
import { version } from '../package.json';
import { client } from '@/api/Client.gen';
import { useLocaleStore } from '@/store/useLocaleStore';
import { useToastStore } from '@/store/useToastStore';
import { useSystemConfigurationStore } from '@/store/useSystemConfigurationStore';

declare global {
  interface Window {
    app: {
      authStore: Store<string>;
      systemConfigurationStore: Store<string>;
    };
  }
}

export const app = createApp(App);

if (SENTRY_URL) {
  Sentry.init({
    dsn: SENTRY_URL,
    environment: SENTRY_ENV,
    release: version,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Performance Monitoring
    tracesSampleRate: 1, //  Capture 100% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

app.use(pinia);
app.use(i18n);
app.use(router);
app.use(vuetify);
app.use(vue3GoogleLogin, {
  clientId: GOOGLE_CLIENT_ID,
});

app.component('VueCropper', VueCropper as any);

const localeStore = useLocaleStore();
const authStore = useAuthStore();
const toastStore = useToastStore();
const systemConfigurationStore = useSystemConfigurationStore();

client.interceptors.request.use(request => {
  if (authStore.logged || authStore.partiallyLogged) {
    request.headers.set('Authorization', `Bearer ${authStore.token}`);
  }
  request.headers.set('Accept-Language', localeStore.locale);
  return request;
});

client.interceptors.response.use(async response => {
  if (response.ok) {
    return response;
  }
  if (!response) {
    toastStore.error({
      message: i18n.global.t('globalErrors.ERR_CONNECTION_REFUSED'),
    });
  } else if (response.status === 500) {
    toastStore.error({
      message: i18n.global.t('globalErrors.SERVER_ERROR'),
    });
  } else {
    const data = await response.json();
    throw new ApiClientError('API Error', data);
  }
  return response;
});

client.setConfig({
  baseUrl: SERVER_URL,
  throwOnError: true,
});

app.mount('#app');

globalThis.app.authStore = authStore;
globalThis.app.systemConfigurationStore = systemConfigurationStore;

window.addEventListener('pageshow', event => {
  if (event.persisted) {
    window.location.reload();
  }
});
